import React, { useRef, useEffect } from 'react';
import { Bell, Check, X, BellOff, Info, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Notification } from '../hooks/useNotifications';

interface NotificationDropdownProps {
    isOpen: boolean;
    onClose: () => void;
    notifications: Notification[];
    unreadCount: number;
    onMarkAsRead: (id: string) => void;
    onMarkAllAsRead: () => void;
    onDelete: (id: string) => void;
    loading: boolean;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
    isOpen,
    onClose,
    notifications,
    unreadCount,
    onMarkAsRead,
    onMarkAllAsRead,
    onDelete,
    loading
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'kyc_approved':
            case 'photo_approved':
            case 'transaction_success':
            case 'wallet_funded':
                return <CheckCircle className="h-5 w-5 text-green-500" />;
            case 'kyc_rejected':
            case 'photo_rejected':
            case 'transaction_failed':
                return <XCircle className="h-5 w-5 text-red-500" />;
            case 'credit_limit_updated':
            case 'status_changed':
            case 'support_response':
                return <Info className="h-5 w-5 text-blue-500" />;
            default:
                return <Bell className="h-5 w-5 text-gray-500" />;
        }
    };

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now.getTime() - date.getTime();

        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (minutes < 1) return 'Just now';
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days === 1) return 'Yesterday';
        return date.toLocaleDateString();
    };

    if (!isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
        >
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                <h3 className="font-semibold text-gray-900">Notifications</h3>
                <div className="flex items-center space-x-3">
                    {unreadCount > 0 && (
                        <button
                            onClick={onMarkAllAsRead}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700"
                        >
                            Mark all as read
                        </button>
                    )}
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto">
                {loading && notifications.length === 0 ? (
                    <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="p-12 text-center">
                        <BellOff className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No notifications yet</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {notifications.map((n) => (
                            <div
                                key={n.id}
                                className={`p-4 hover:bg-gray-50 transition-colors relative group ${!n.isRead ? 'bg-blue-50/30' : ''}`}
                            >
                                <div className="flex items-start space-x-3">
                                    <div className="mt-1 flex-shrink-0">
                                        {getIcon(n.type)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <p className={`text-sm font-semibold truncate ${!n.isRead ? 'text-gray-900' : 'text-gray-700'}`}>
                                                {n.title}
                                            </p>
                                            {!n.isRead && (
                                                <span className="h-2 w-2 rounded-full bg-blue-600 flex-shrink-0 ml-2"></span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed">
                                            {n.message}
                                        </p>
                                        <div className="flex items-center mt-2 text-[11px] text-gray-400 space-x-2">
                                            <Clock className="h-3 w-3" />
                                            <span>{formatTime(n.createdAt)}</span>
                                            {n.priority === 'high' || n.priority === 'urgent' && (
                                                <span className="px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-bold uppercase tracking-wider">
                                                    {n.priority}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {!n.isRead && (
                                            <button
                                                onClick={() => onMarkAsRead(n.id)}
                                                className="p-1 hover:bg-white rounded border border-gray-100 shadow-sm text-blue-600"
                                                title="Mark as read"
                                            >
                                                <Check className="h-3 w-3" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => onDelete(n.id)}
                                            className="p-1 hover:bg-white rounded border border-gray-100 shadow-sm text-red-500"
                                            title="Delete"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {notifications.length > 0 && (
                <div className="p-3 border-t border-gray-100 bg-gray-50 text-center">
                    <button className="text-xs font-semibold text-gray-600 hover:text-gray-900">
                        View all notifications
                    </button>
                </div>
            )}
        </div>
    );
};

const XCircle: React.FC<{ className?: string }> = ({ className }) => (
    <AlertCircle className={className} />
);
